import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import type { FormSchema, FormField, ThemeSetting, CustomTheme, RankingItem } from '../../types/form';
import { evaluateConditionalLogicFull, getDefaultFieldState } from '../../lib/conditionalEngine';
import { submitForm, getPresignedUrl, uploadFile } from '../../lib/api';

// Signature Pad Component
interface SignaturePadProps {
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  className?: string;
}

function SignaturePad({ onChange, disabled, className }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    return ctx;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Fill with white background
    const ctx = getContext();
    if (ctx) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [getContext]);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    const ctx = getContext();
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;
    const ctx = getContext();
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing && hasSignature) {
      const canvas = canvasRef.current;
      if (canvas) {
        onChange(canvas.toDataURL('image/png'));
      }
    }
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange(null);
  };

  return (
    <div className={className}>
      <canvas
        ref={canvasRef}
        className={`w-full h-32 border rounded-lg cursor-crosshair ${disabled ? 'bg-gray-100' : 'bg-white'}`}
        style={{ touchAction: 'none' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      {hasSignature && !disabled && (
        <button
          type="button"
          onClick={clear}
          className="mt-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Clear signature
        </button>
      )}
    </div>
  );
}

// Ranking Field Component with drag and drop
interface RankingFieldProps {
  value: RankingItem[];
  onChange: (items: RankingItem[]) => void;
  disabled?: boolean;
}

function RankingField({ value, onChange, disabled }: RankingFieldProps) {
  const [items, setItems] = useState<RankingItem[]>(value);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    setItems(value);
  }, [value]);

  const handleDragStart = (index: number) => {
    if (disabled) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || disabled) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    onChange(items);
  };

  const moveItem = (fromIndex: number, direction: 'up' | 'down') => {
    if (disabled) return;
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= items.length) return;

    const newItems = [...items];
    [newItems[fromIndex], newItems[toIndex]] = [newItems[toIndex], newItems[fromIndex]];
    setItems(newItems);
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={item.id}
          draggable={!disabled}
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-3 p-3 bg-white border rounded-lg ${
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-grab active:cursor-grabbing'
          } ${draggedIndex === index ? 'opacity-50 border-[var(--theme-primary)]' : 'border-[var(--theme-border)]'}`}
        >
          <span
            className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium"
            style={{ backgroundColor: 'var(--theme-primary)', color: 'white' }}
          >
            {index + 1}
          </span>
          <span className="flex-1" style={{ color: 'var(--theme-text)' }}>
            {item.label}
          </span>
          {!disabled && (
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => moveItem(index, 'up')}
                disabled={index === 0}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move up"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => moveItem(index, 'down')}
                disabled={index === items.length - 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move down"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      ))}
    </div>
  );
}

interface FormRendererProps {
  formId: string;
  schema: FormSchema;
  onSuccess?: (result: { submissionId: string; message: string; redirectUrl?: string }) => void;
  onError?: (error: Error) => void;
}

function getThemeStyles(theme: ThemeSetting): React.CSSProperties {
  if (theme === 'light') {
    return {
      '--theme-primary': '#3b82f6',
      '--theme-bg': '#ffffff',
      '--theme-text': '#1f2937',
      '--theme-border': '#d1d5db',
      '--theme-error': '#dc2626',
      '--theme-success': '#16a34a',
    } as React.CSSProperties;
  }

  if (theme === 'dark') {
    return {
      '--theme-primary': '#60a5fa',
      '--theme-bg': '#1f2937',
      '--theme-text': '#f9fafb',
      '--theme-border': '#4b5563',
      '--theme-error': '#f87171',
      '--theme-success': '#4ade80',
    } as React.CSSProperties;
  }

  // Custom theme
  const custom = theme as CustomTheme;
  return {
    '--theme-primary': custom.primary || '#3b82f6',
    '--theme-bg': custom.background || '#ffffff',
    '--theme-text': custom.text || '#1f2937',
    '--theme-border': custom.border || '#d1d5db',
    '--theme-error': custom.error || '#dc2626',
    '--theme-success': custom.success || '#16a34a',
  } as React.CSSProperties;
}

export default function FormRenderer({ formId, schema, onSuccess, onError }: FormRendererProps) {
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    mode: 'onChange', // Real-time validation as user types
    reValidateMode: 'onChange',
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>({});
  const [signatures, setSignatures] = useState<Record<string, string | null>>({});

  const watchedValues = watch();

  // Build base required map from field definitions
  const baseRequired = useMemo(() => {
    const required: Record<string, boolean> = {};
    schema.fields.forEach((field) => {
      required[field.id] = field.required;
    });
    return required;
  }, [schema.fields]);

  // Evaluate conditional logic
  const { fieldStates, pageNavigations } = useMemo(() => {
    return evaluateConditionalLogicFull(schema.conditionalLogic, watchedValues, baseRequired, schema.fields);
  }, [schema.conditionalLogic, watchedValues, baseRequired, schema.fields]);

  // Detect pages from page_break fields
  const pages = useMemo(() => {
    const pageList: { id: string; title: string; description?: string; startIndex: number; endIndex: number }[] = [];
    let currentPageStart = 0;

    schema.fields.forEach((field, index) => {
      if (field.type === 'page_break') {
        // Close previous page
        if (index > currentPageStart || pageList.length === 0) {
          pageList.push({
            id: pageList.length === 0 ? 'page_0' : field.id,
            title: pageList.length === 0 ? 'Page 1' : field.label || `Page ${pageList.length + 1}`,
            description: field.properties.pageDescription,
            startIndex: currentPageStart,
            endIndex: index,
          });
        }
        currentPageStart = index + 1;
      }
    });

    // Add final page if there are remaining fields
    if (currentPageStart < schema.fields.length || pageList.length === 0) {
      const lastPageBreak = schema.fields.filter(f => f.type === 'page_break').pop();
      pageList.push({
        id: lastPageBreak ? `page_after_${lastPageBreak.id}` : 'page_0',
        title: pageList.length === 0 ? '' : lastPageBreak?.label || `Page ${pageList.length + 1}`,
        description: lastPageBreak?.properties.pageDescription,
        startIndex: currentPageStart,
        endIndex: schema.fields.length,
      });
    }

    return pageList;
  }, [schema.fields]);

  const hasPages = pages.length > 1;
  const currentPageData = pages[currentStep] || pages[0];

  // Legacy multi-step support
  const isMultiStep = schema.steps && schema.steps.length > 0;
  const steps = schema.steps || [];
  const currentStepData = steps[currentStep];

  // Get fields for current view
  const visibleFields = useMemo(() => {
    // Use page_break-based pages first
    if (hasPages && currentPageData) {
      return schema.fields.slice(currentPageData.startIndex, currentPageData.endIndex);
    }
    // Fall back to legacy step-based multi-step
    if (isMultiStep && currentStepData) {
      return schema.fields.filter((f) => currentStepData.fieldIds.includes(f.id));
    }
    return schema.fields;
  }, [schema.fields, hasPages, currentPageData, isMultiStep, currentStepData]);

  async function handleFileUpload(field: FormField, files: FileList) {
    const file = files[0];
    if (!file) return;

    try {
      // Get presigned URL
      const presign = await getPresignedUrl({
        formId,
        fieldId: field.id,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        fieldType: 'file',
      });

      // Upload file
      await uploadFile(presign.uploadId, file, (progress) => {
        setUploadProgress((prev) => ({ ...prev, [field.id]: progress }));
      });

      // Store upload ID
      setUploadedFiles((prev) => ({ ...prev, [field.name]: presign.uploadId }));
      setUploadProgress((prev) => ({ ...prev, [field.id]: 100 }));
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress((prev) => ({ ...prev, [field.id]: -1 }));
    }
  }

  async function onSubmit(data: Record<string, unknown>) {
    try {
      // Merge uploaded file IDs and signatures into data
      const submissionData = { ...data };
      const uploadIds: string[] = [];

      Object.entries(uploadedFiles).forEach(([fieldName, uploadId]) => {
        submissionData[fieldName] = uploadId;
        uploadIds.push(uploadId);
      });

      // Add signatures to submission data
      Object.entries(signatures).forEach(([fieldName, signatureData]) => {
        if (signatureData) {
          submissionData[fieldName] = signatureData;
        }
      });

      const result = await submitForm(formId, submissionData, uploadIds);

      if (onSuccess) {
        onSuccess(result);
      }

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      }
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error : new Error('Submission failed'));
      }
    }
  }

  function nextStep() {
    // Check for conditional page navigation (skip_to_page)
    const triggeredNavigation = pageNavigations.find(nav => nav.triggered);
    if (triggeredNavigation && hasPages) {
      const targetIndex = pages.findIndex(p => p.id === triggeredNavigation.skipToPageId);
      if (targetIndex !== -1 && targetIndex > currentStep) {
        setCurrentStep(targetIndex);
        window.scrollTo(0, 0);
        return;
      }
    }

    const maxSteps = hasPages ? pages.length : steps.length;
    if (currentStep < maxSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  }

  function prevStep() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  }

  function goToPage(pageIndex: number) {
    if (pageIndex >= 0 && pageIndex < pages.length) {
      setCurrentStep(pageIndex);
      window.scrollTo(0, 0);
    }
  }

  // Helper to get validation message with custom override
  function getValidationMessage(field: FormField, type: 'required' | 'minLength' | 'maxLength' | 'min' | 'max' | 'pattern' | 'email' | 'url', defaultMsg: string): string {
    return field.properties.validationMessages?.[type] || defaultMsg;
  }

  function renderField(field: FormField) {
    const state = fieldStates[field.id] || getDefaultFieldState(field.required);

    if (!state.visible) return null;

    const isRequired = state.required;
    const isDisabled = !state.enabled;

    const fieldError = errors[field.name];

    // Build validation rules with custom messages
    const validationRules: Record<string, unknown> = {
      required: isRequired ? getValidationMessage(field, 'required', `${field.label} is required`) : false,
      disabled: isDisabled,
    };

    // Add type-specific validation
    if (field.type === 'text' || field.type === 'textarea') {
      if (field.properties.minLength) {
        validationRules.minLength = {
          value: field.properties.minLength,
          message: getValidationMessage(field, 'minLength', `Must be at least ${field.properties.minLength} characters`),
        };
      }
      if (field.properties.maxLength) {
        validationRules.maxLength = {
          value: field.properties.maxLength,
          message: getValidationMessage(field, 'maxLength', `Must be no more than ${field.properties.maxLength} characters`),
        };
      }
      if (field.properties.pattern) {
        validationRules.pattern = {
          value: new RegExp(field.properties.pattern),
          message: getValidationMessage(field, 'pattern', 'Invalid format'),
        };
      }
    }

    if (field.type === 'number') {
      if (field.properties.min !== undefined) {
        validationRules.min = {
          value: field.properties.min,
          message: getValidationMessage(field, 'min', `Must be at least ${field.properties.min}`),
        };
      }
      if (field.properties.max !== undefined) {
        validationRules.max = {
          value: field.properties.max,
          message: getValidationMessage(field, 'max', `Must be no more than ${field.properties.max}`),
        };
      }
    }

    if (field.type === 'email') {
      validationRules.pattern = {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: getValidationMessage(field, 'email', 'Please enter a valid email address'),
      };
    }

    const commonProps = {
      ...register(field.name, validationRules),
      placeholder: field.placeholder,
      disabled: isDisabled,
      className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] ${
        fieldError ? 'border-[var(--theme-error)]' : 'border-[var(--theme-border)]'
      } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'}`,
    };

    // For layout fields, render differently
    if (field.type === 'section') {
      return (
        <div key={field.id} className="mb-6 mt-6">
          <div className="border-l-4 border-[var(--theme-primary)] pl-4 py-3">
            <h3 className="font-semibold text-lg" style={{ color: 'var(--theme-text)' }}>
              {field.label}
            </h3>
            {field.properties.sectionDescription && (
              <p className="text-sm mt-1" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
                {field.properties.sectionDescription}
              </p>
            )}
          </div>
        </div>
      );
    }

    if (field.type === 'divider') {
      return (
        <div key={field.id} className="my-6">
          <hr
            style={{
              borderStyle: field.properties.dividerStyle || 'solid',
              borderColor: field.properties.dividerColor || 'var(--theme-border)',
            }}
          />
        </div>
      );
    }

    // page_break is handled at a higher level for pagination, skip rendering
    if (field.type === 'page_break') {
      return null;
    }

    return (
      <div key={field.id} className="mb-4">
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
          {field.label}
          {isRequired && <span className="text-[var(--theme-error)] ml-1">*</span>}
        </label>

        {field.type === 'text' && <input type="text" {...commonProps} />}

        {field.type === 'email' && <input type="email" {...commonProps} />}

        {field.type === 'number' && (
          <input
            type="number"
            {...commonProps}
            min={field.properties.min}
            max={field.properties.max}
            step={field.properties.step}
          />
        )}

        {field.type === 'textarea' && <textarea {...commonProps} rows={4} />}

        {field.type === 'select' && (
          <select {...commonProps} multiple={field.properties.multiple}>
            <option value="">Select an option...</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {field.type === 'checkbox' && (
          <div className="space-y-2">
            {field.options?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={opt.value}
                  {...register(field.name)}
                  disabled={isDisabled}
                  className="rounded border-[var(--theme-border)]"
                />
                <span style={{ color: 'var(--theme-text)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'radio' && (
          <div className="space-y-2">
            {field.options?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  value={opt.value}
                  {...register(field.name, { required: isRequired ? `${field.label} is required` : false })}
                  disabled={isDisabled}
                  className="border-[var(--theme-border)]"
                />
                <span style={{ color: 'var(--theme-text)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'date' && <input type="date" {...commonProps} />}

        {field.type === 'datetime' && <input type="datetime-local" {...commonProps} />}

        {field.type === 'file' && (
          <div>
            <input
              type="file"
              accept={field.properties.accept}
              onChange={(e) => e.target.files && handleFileUpload(field, e.target.files)}
              disabled={isDisabled}
              className={commonProps.className}
            />
            {uploadProgress[field.id] !== undefined && uploadProgress[field.id] >= 0 && (
              <div className="mt-2">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--theme-primary)] transition-all"
                    style={{ width: `${uploadProgress[field.id]}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {uploadProgress[field.id] === 100 ? 'Uploaded' : `${uploadProgress[field.id]}%`}
                </span>
              </div>
            )}
          </div>
        )}

        {field.type === 'signature' && (
          <SignaturePad
            disabled={isDisabled}
            onChange={(dataUrl) => {
              setSignatures((prev) => ({ ...prev, [field.name]: dataUrl }));
              setValue(field.name, dataUrl || '');
            }}
          />
        )}

        {field.type === 'richtext' && (
          <textarea
            {...commonProps}
            rows={6}
            placeholder={field.placeholder || 'Enter formatted text...'}
          />
        )}

        {field.type === 'phone' && (
          <div className="flex gap-2">
            <select
              {...register(`${field.name}_country`)}
              disabled={isDisabled}
              className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] border-[var(--theme-border)] ${isDisabled ? 'bg-gray-100' : 'bg-[var(--theme-bg)]'}`}
              style={{ width: '120px' }}
            >
              <option value="+1">+1 (US)</option>
              <option value="+44">+44 (UK)</option>
              <option value="+972">+972 (IL)</option>
              <option value="+49">+49 (DE)</option>
              <option value="+33">+33 (FR)</option>
              <option value="+81">+81 (JP)</option>
              <option value="+86">+86 (CN)</option>
              <option value="+91">+91 (IN)</option>
              <option value="+55">+55 (BR)</option>
              <option value="+61">+61 (AU)</option>
            </select>
            <input
              type="tel"
              {...register(field.name, {
                ...validationRules,
                pattern: {
                  value: /^[0-9\s\-()]+$/,
                  message: 'Please enter a valid phone number',
                },
              })}
              placeholder={field.placeholder || 'Phone number'}
              disabled={isDisabled}
              className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] ${
                fieldError ? 'border-[var(--theme-error)]' : 'border-[var(--theme-border)]'
              } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'}`}
            />
          </div>
        )}

        {field.type === 'url' && (
          <input
            type="url"
            {...register(field.name, {
              ...validationRules,
              pattern: {
                value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                message: getValidationMessage(field, 'url', 'Please enter a valid URL'),
              },
            })}
            placeholder={field.placeholder || 'https://example.com'}
            disabled={isDisabled}
            className={commonProps.className}
          />
        )}

        {field.type === 'rating' && (
          <div className="flex items-center gap-1">
            {Array.from({ length: field.properties.ratingMax || 5 }).map((_, index) => {
              const value = index + 1;
              const icon = field.properties.ratingIcon || 'star';
              return (
                <label key={index} className="cursor-pointer">
                  <input
                    type="radio"
                    value={value}
                    {...register(field.name, validationRules)}
                    disabled={isDisabled}
                    className="sr-only"
                  />
                  <span
                    className={`text-2xl transition-colors ${
                      Number(watchedValues[field.name]) >= value
                        ? 'text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-200'
                    }`}
                  >
                    {icon === 'star' ? '★' : icon === 'heart' ? '♥' : '●'}
                  </span>
                </label>
              );
            })}
            {field.properties.showValue !== false && watchedValues[field.name] && (
              <span className="ml-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                {watchedValues[field.name]} / {field.properties.ratingMax || 5}
              </span>
            )}
          </div>
        )}

        {field.type === 'slider' && (
          <div className="space-y-2">
            <input
              type="range"
              {...register(field.name, validationRules)}
              min={field.properties.sliderMin ?? 0}
              max={field.properties.sliderMax ?? 100}
              step={field.properties.sliderStep ?? 1}
              disabled={isDisabled}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--theme-primary)]"
            />
            <div className="flex justify-between text-xs" style={{ color: 'var(--theme-text)', opacity: 0.6 }}>
              {field.properties.showMinMax !== false && (
                <>
                  <span>{field.properties.sliderMin ?? 0}</span>
                  {field.properties.showValue !== false && (
                    <span className="font-medium" style={{ opacity: 1 }}>
                      {watchedValues[field.name] ?? field.properties.sliderMin ?? 0}
                    </span>
                  )}
                  <span>{field.properties.sliderMax ?? 100}</span>
                </>
              )}
              {field.properties.showMinMax === false && field.properties.showValue !== false && (
                <span className="font-medium w-full text-center" style={{ opacity: 1 }}>
                  {watchedValues[field.name] ?? field.properties.sliderMin ?? 0}
                </span>
              )}
            </div>
          </div>
        )}

        {field.type === 'color' && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <input
                type="color"
                {...register(field.name, validationRules)}
                disabled={isDisabled}
                className="w-12 h-10 rounded cursor-pointer border border-[var(--theme-border)]"
              />
              <input
                type="text"
                value={watchedValues[field.name] || '#000000'}
                onChange={(e) => setValue(field.name, e.target.value)}
                disabled={isDisabled}
                placeholder="#000000"
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] border-[var(--theme-border)] ${isDisabled ? 'bg-gray-100' : 'bg-[var(--theme-bg)]'}`}
              />
            </div>
            {field.properties.colorPresets && field.properties.colorPresets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {field.properties.colorPresets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setValue(field.name, color)}
                    disabled={isDisabled}
                    className={`w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${
                      watchedValues[field.name] === color ? 'border-[var(--theme-primary)]' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {field.type === 'hidden' && (
          <input
            type="hidden"
            {...register(field.name)}
            value={field.properties.hiddenValue || ''}
          />
        )}

        {field.type === 'matrix' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2"></th>
                  {field.properties.matrixColumns?.map((col) => (
                    <th
                      key={col.id}
                      className="text-center p-2 text-sm font-medium"
                      style={{ color: 'var(--theme-text)' }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {field.properties.matrixRows?.map((row) => (
                  <tr key={row.id} className="border-t border-[var(--theme-border)]">
                    <td className="p-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                      {row.label}
                    </td>
                    {field.properties.matrixColumns?.map((col) => (
                      <td key={col.id} className="text-center p-2">
                        <input
                          type={field.properties.matrixType === 'multiple' ? 'checkbox' : 'radio'}
                          {...register(`${field.name}.${row.id}`)}
                          value={col.value}
                          disabled={isDisabled}
                          className="accent-[var(--theme-primary)]"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {field.type === 'ranking' && (
          <RankingField
            value={(watchedValues[field.name] as RankingItem[]) || field.properties.rankingItems || []}
            onChange={(items) => setValue(field.name, items)}
            disabled={isDisabled}
          />
        )}

        {field.helpText && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-text)', opacity: 0.6 }}>
            {field.helpText}
          </p>
        )}

        {fieldError && (
          <p className="text-xs mt-1 text-[var(--theme-error)]">
            {fieldError.message as string}
          </p>
        )}
      </div>
    );
  }

  const themeStyles = getThemeStyles(schema.settings.theme || 'light');
  const isDark = schema.settings.theme === 'dark';

  return (
    <div
      className={`max-w-xl mx-auto p-6 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
      style={themeStyles}
    >
      {/* Page Progress (for page_break-based multi-page forms) */}
      {hasPages && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {pages.map((page, index) => (
              <div key={page.id} className="flex items-center flex-1">
                <button
                  type="button"
                  onClick={() => goToPage(index)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index <= currentStep
                      ? 'bg-[var(--theme-primary)] text-white'
                      : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                  }`}
                >
                  {index + 1}
                </button>
                {index < pages.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index < currentStep ? 'bg-[var(--theme-primary)]' : 'bg-gray-200'
                    }`}
                    style={{ minWidth: '20px' }}
                  />
                )}
              </div>
            ))}
          </div>
          {currentPageData?.title && (
            <div className="text-center">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
                {currentPageData.title}
              </h2>
              {currentPageData.description && (
                <p className="text-sm" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
                  {currentPageData.description}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Multi-step Progress (legacy step-based) */}
      {!hasPages && isMultiStep && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index <= currentStep
                      ? 'bg-[var(--theme-primary)] text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-full h-1 mx-2 ${
                      index < currentStep ? 'bg-[var(--theme-primary)]' : 'bg-gray-200'
                    }`}
                    style={{ minWidth: '40px' }}
                  />
                )}
              </div>
            ))}
          </div>
          {currentStepData && (
            <div className="text-center">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
                {currentStepData.title}
              </h2>
              {currentStepData.description && (
                <p className="text-sm" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
                  {currentStepData.description}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit, () => setShowValidationSummary(true))}>
        {/* Validation Summary */}
        {showValidationSummary && Object.keys(errors).length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="font-medium text-red-800 mb-2">Please fix the following errors:</h4>
                <ul className="space-y-1">
                  {Object.entries(errors).map(([fieldName, error]) => {
                    const field = schema.fields.find(f => f.name === fieldName);
                    return (
                      <li key={fieldName} className="text-sm text-red-700">
                        <button
                          type="button"
                          onClick={() => {
                            const element = document.querySelector(`[name="${fieldName}"]`);
                            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            (element as HTMLElement)?.focus();
                          }}
                          className="hover:underline text-left"
                        >
                          <span className="font-medium">{field?.label || fieldName}:</span> {(error as { message?: string })?.message || 'Invalid value'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setShowValidationSummary(false)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {visibleFields.map(renderField)}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {/* Page-based navigation */}
          {hasPages && currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex-1 px-4 py-2 border border-[var(--theme-border)] rounded-lg hover:bg-gray-50"
              style={{ color: 'var(--theme-text)' }}
            >
              Previous
            </button>
          )}

          {hasPages && currentStep < pages.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90"
            >
              Next
            </button>
          ) : hasPages ? (
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : schema.settings.submitButtonText || 'Submit'}
            </button>
          ) : null}

          {/* Legacy step-based navigation */}
          {!hasPages && isMultiStep && currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex-1 px-4 py-2 border border-[var(--theme-border)] rounded-lg hover:bg-gray-50"
              style={{ color: 'var(--theme-text)' }}
            >
              Previous
            </button>
          )}

          {!hasPages && isMultiStep && currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90"
            >
              Next
            </button>
          ) : !hasPages ? (
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : schema.settings.submitButtonText || 'Submit'}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
